'use client';

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Bot, 
  User, 
  Play, 
  Pause,
  RotateCcw, 
  ArrowRight,
  Swords,
  Trophy,
  Coins
} from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface DemoWord {
  word: string;
  player: 'USER' | 'OPPONENT';
  scoreAwarded: number;
}

const DIGRAPHS = ['dh', 'gj', 'll', 'nj', 'rr', 'sh', 'th', 'xh', 'zh'];

function extractLastUnit(word: string): string {
  if (!word) return '';
  const lower = word.toLowerCase();
  if (lower.length >= 2) {
    const lastTwo = lower.slice(-2);
    if (DIGRAPHS.includes(lastTwo)) {
      return word.slice(-2);
    }
  }
  return word.slice(-1);
}

const DEMO_SEQUENCE: DemoWord[] = [
  { word: 'LOJA', player: 'USER', scoreAwarded: 40 },
  { word: 'ALFABETI', player: 'OPPONENT', scoreAwarded: 80 },
  { word: 'ISHULL', player: 'USER', scoreAwarded: 40 },
  { word: 'LLULLA', player: 'OPPONENT', scoreAwarded: 40 },
  { word: 'AMBICIOZ', player: 'USER', scoreAwarded: 80 },
  { word: 'ZEBRA', player: 'OPPONENT', scoreAwarded: 50 },
];

const TIMINGS = {
  INITIAL: 3000,
  TYPING: 350,
  TRANSITION: 4000, 
  RESTART: 6000,    
};

type DemoState = 'PLAYING' | 'SCORING' | 'WON';

export const GameDemoOverlay: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ 
  isOpen, 
  onClose 
}) => {
  const [history, setHistory] = useState<DemoWord[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [typingWord, setTypingWord] = useState('');
  const [isPaused, setIsPaused] = useState(false);
  const [userScore, setUserScore] = useState(0);
  const [opponentScore, setOpponentScore] = useState(0);
  const [demoState, setDemoState] = useState<DemoState>('PLAYING');
  
  const scrollRef = useRef<HTMLDivElement>(null);
  const isPausedRef = useRef(isPaused);
  const isOpenRef = useRef(isOpen);
  const currentIndexRef = useRef(0);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen) {
      resetDemo();
    } else {
      resetDemo();
    }
  }, [isOpen]);

  const resetDemo = () => {
    setHistory([]);
    setTypingWord('');
    setIsTyping(false);
    setUserScore(0);
    setOpponentScore(0);
    setDemoState('PLAYING');
    currentIndexRef.current = 0;
    if (isOpenRef.current) runSequence(0);
  };

  const waitWhilePaused = async () => {
    while (isPausedRef.current && isOpenRef.current) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  };

  const runSequence = async (index: number) => {
    if (!isOpenRef.current) return;

    if (index >= DEMO_SEQUENCE.length) {
      setDemoState('SCORING');
      
      let timer = 0;
      while (timer < TIMINGS.TRANSITION && isOpenRef.current) {
        await waitWhilePaused();
        await new Promise(resolve => setTimeout(resolve, 100));
        if (!isPausedRef.current) timer += 100;
      }

      setDemoState('WON');
      
      let restartTimer = 0;
      while (restartTimer < TIMINGS.RESTART && isOpenRef.current) {
        await waitWhilePaused();
        await new Promise(resolve => setTimeout(resolve, 100));
        if (!isPausedRef.current) restartTimer += 100;
      }
      
      if (isOpenRef.current) {
        resetDemo();
      }
      return;
    }

    const nextWord = DEMO_SEQUENCE[index];
    
    const waitTime = index === 0 ? TIMINGS.INITIAL : TIMINGS.TRANSITION;
    let timer = 0;
    while (timer < waitTime && isOpenRef.current) {
      await waitWhilePaused();
      await new Promise(resolve => setTimeout(resolve, 100));
      if (!isPausedRef.current) timer += 100;
    }

    if (!isOpenRef.current) return;

    setIsTyping(true);
    for (let i = 1; i <= nextWord.word.length; i++) {
      await waitWhilePaused();
      if (!isOpenRef.current) return;
      
      setTypingWord(nextWord.word.substring(0, i));
      await new Promise(resolve => setTimeout(resolve, TIMINGS.TYPING));
    }
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (!isOpenRef.current) return;
    
    setIsTyping(false);
    setTypingWord('');
    setHistory(prev => [...prev, nextWord]);
    
    if (nextWord.player === 'USER') {
      setUserScore(prev => prev + nextWord.scoreAwarded);
    } else {
      setOpponentScore(prev => prev + nextWord.scoreAwarded);
    }

    currentIndexRef.current = index + 1;
    runSequence(index + 1);
  };

  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        scrollRef.current?.scrollTo({
          left: scrollRef.current.scrollWidth,
          behavior: 'smooth'
        });
      }, 50);
    }
  }, [history, typingWord, isTyping]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 md:p-4 bg-slate-900/70 backdrop-blur-md">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="relative w-full max-w-2xl bg-background doodle-border shadow-doodle-xl overflow-hidden flex flex-col h-[640px] md:h-[820px] max-h-[95vh]"
      >
        {/* Header */}
        <div className="p-3 md:p-6 border-b-2 border-border-main/10 flex items-center justify-between bg-card shrink-0">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-rose-100 dark:bg-rose-900/30 rounded-full doodle-border-soft flex items-center justify-center text-rose-500 shadow-sm">
              <Play className="w-4 h-4 md:w-5 md:h-5 fill-current" />
            </div>
            <div>
              <h2 className="font-black handwritten text-lg md:text-2xl text-foreground leading-none tracking-tight uppercase">DEMO E LOJËS</h2>
              <p className="text-[8px] md:text-xs font-bold text-muted-foreground uppercase tracking-widest hidden xs:block">Mëso si luhet</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <motion.button 
              onClick={() => setIsPaused(!isPaused)}
              whileHover={{ scale: 1.05, x: -2, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className={cn(
                "p-2 md:p-3 bg-card doodle-border transition-all flex items-center gap-1.5 md:gap-2 min-w-[90px] md:min-w-[120px] justify-center relative overflow-hidden group boing",
                isPaused ? "shadow-[3px_3px_0px_0px_#0ea5e9] text-sky-600 dark:text-sky-400" : "shadow-[3px_3px_0px_0px_#f43f5e] text-rose-600 dark:text-rose-400"
              )}
            >
              {isPaused ? <Play className="w-4 h-4 md:w-5 md:h-5 fill-current" /> : <Pause className="w-4 h-4 md:w-5 md:h-5 fill-current" />}
              <span className="font-black handwritten text-xs md:text-sm uppercase z-10">
                {isPaused ? 'Vazhdo' : 'Ndalo'}
              </span>
              <motion.div 
                className="absolute inset-0 opacity-0 group-hover:opacity-10 bg-gradient-to-r from-pink-400 via-sky-400 to-amber-400"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              />
            </motion.button>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-full transition-colors boing"
            >
              <X className="w-5 h-5 md:w-6 md:h-6 text-muted-foreground/60" />
            </button>
          </div>
        </div>

        {/* Game Area */}
        <div className="flex-1 overflow-hidden flex flex-col bg-muted/10 relative">
          {/* Players Info with Scores */}
          <div className="p-3 md:p-4 flex justify-between items-start gap-2 md:gap-4 shrink-0">
            <div className={cn(
              "flex flex-col gap-1.5 md:gap-2 bg-rose-50/90 p-2 md:p-3 doodle-border-soft shadow-sm transition-all border-rose-300 dark:border-rose-900/50 min-w-[100px] md:min-w-[140px]",
              (currentIndexRef.current % 2 === 0 && demoState === 'PLAYING') ? "ring-2 md:ring-4 ring-rose-400" : "opacity-60 grayscale-[0.2]"
            )}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 md:w-9 md:h-9 bg-rose-100 dark:bg-rose-900/40 rounded-lg flex items-center justify-center text-rose-500">
                  <User className="w-4 h-4 md:w-5 md:h-5" />
                </div>
                <div className="text-left">
                  <div className="text-[6px] md:text-[8px] font-black text-rose-400 dark:text-rose-500 uppercase leading-none">Lojtari</div>
                  <div className="text-[9px] md:text-xs font-black handwritten text-rose-600 dark:text-rose-400 uppercase">TI (UNË)</div>
                </div>
              </div>
              <div className="pt-1 border-t border-dashed border-rose-200 dark:border-rose-900/50 flex justify-between items-baseline">
                <span className="text-[7px] md:text-[8px] font-black text-rose-400 dark:text-rose-500 uppercase handwritten">Pikët:</span>
                <motion.span 
                  key={userScore}
                  initial={{ scale: 1.5, color: '#f43f5e' }}
                  animate={{ scale: 1, color: '#e11d48' }}
                  className="text-base md:text-xl font-black text-rose-600 dark:text-rose-500"
                >
                  {userScore}
                </motion.span>
              </div>
            </div>

            <div className="flex flex-col items-center pt-3">
              <Swords className="w-4 h-4 md:w-6 md:h-6 text-rose-500 animate-pulse" />
              <div className="text-[7px] md:text-[9px] font-black text-muted-foreground/40 uppercase mt-1 tracking-widest">SFIDA</div>
            </div>

            <div className={cn(
              "flex flex-col gap-1.5 md:gap-2 bg-sky-50/90 p-2 md:p-3 doodle-border-soft shadow-sm transition-all border-sky-300 dark:border-sky-900/50 min-w-[100px] md:min-w-[140px] text-right",
              (currentIndexRef.current % 2 !== 0 && demoState === 'PLAYING') ? "ring-2 md:ring-4 ring-sky-400" : "opacity-60 grayscale-[0.2]"
            )}>
              <div className="flex items-center gap-2 flex-row-reverse">
                <div className="w-7 h-7 md:w-9 md:h-9 bg-sky-100 dark:bg-sky-900/40 rounded-lg flex items-center justify-center text-sky-500">
                  <Bot className="w-4 h-4 md:w-5 md:h-5" />
                </div>
                <div className="text-right">
                  <div className="text-[6px] md:text-[8px] font-black text-sky-400 dark:text-sky-500 uppercase leading-none">Kundërshtari</div>
                  <div className="text-[9px] md:text-xs font-black handwritten text-sky-600 dark:text-sky-400 uppercase">KUNDËRSHTARI</div>
                </div>
              </div>
              <div className="pt-1 border-t border-dashed border-sky-200 dark:border-sky-900/50 flex justify-between flex-row-reverse items-baseline">
                <span className="text-[7px] md:text-[8px] font-black text-sky-400 dark:text-sky-500 uppercase handwritten">Pikët:</span>
                <motion.span 
                  key={opponentScore}
                  initial={{ scale: 1.5, color: '#0ea5e9' }}
                  animate={{ scale: 1, color: '#0284c7' }}
                  className="text-base md:text-xl font-black text-sky-600 dark:text-sky-500"
                >
                  {opponentScore}
                </motion.span>
              </div>
            </div>
          </div>

          {/* History Area */}
          <div className="h-28 md:h-44 relative flex items-center px-4 overflow-hidden shrink-0 border-y-2 border-border-main/10 bg-muted/20">
            <div 
              ref={scrollRef}
              className="w-full flex items-center gap-3 md:gap-4 overflow-x-auto pb-4 pt-4 no-scrollbar"
            >
              <AnimatePresence mode="popLayout">
                {history.length === 0 && !isTyping && (
                  <motion.div 
                    initial={{ opacity: 0 }} 
                    animate={{ 
                      opacity: [0.3, 1, 0.3],
                      scale: [0.98, 1.02, 0.98]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="w-full text-center"
                  >
                    <span className="text-muted-foreground/30 font-black handwritten text-base md:text-xl italic uppercase tracking-widest">
                      Duke u përgatitur...
                    </span>
                  </motion.div>
                )}
                
                {history.map((item, idx) => {
                  const isLast = idx === history.length - 1;
                  const lastUnit = extractLastUnit(item.word);
                  const wordBody = item.word.substring(0, item.word.length - lastUnit.length);
                  const isUser = item.player === 'USER';
                  
                  return (
                    <motion.div
                      layout
                      key={`${idx}-${item.word}`}
                      initial={{ opacity: 0, x: 20, scale: 0.9 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                      className="relative flex-shrink-0 flex items-center gap-3 md:gap-4"
                    >
                      <div className={cn(
                        "p-3 md:p-5 doodle-border shadow-doodle font-black handwritten text-xl md:text-3xl uppercase tracking-tighter transition-all",
                        isUser ? "bg-card text-sky-500 dark:text-sky-400" : "bg-card text-rose-500 dark:text-rose-400",
                        isLast && demoState === 'PLAYING' && "ring-2 md:ring-4 ring-amber-400 ring-offset-2 ring-offset-background rotate-2"
                      )}>
                        {wordBody}
                        <span className={cn(isUser ? "text-rose-500 dark:text-rose-400" : "text-sky-500 dark:text-sky-400")}>
                          {lastUnit}
                        </span>
                        
                        <motion.div 
                          initial={{ y: 0, opacity: 0 }}
                          animate={{ y: -35, opacity: 1 }}
                          className="absolute -top-3 -right-1 bg-amber-400 text-white text-[8px] md:text-[10px] px-1.5 py-0.5 rounded-full shadow-sm font-black"
                        >
                          +{item.scoreAwarded}
                        </motion.div>
                      </div>
                      
                      {!isLast && (
                        <div className="text-muted-foreground/30">
                          <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                        </div>
                      )}

                      <div className={cn(
                        "absolute -top-2.5 md:-top-3 left-1/2 -translate-x-1/2 px-1.5 md:px-2 py-0.5 rounded text-[6px] md:text-[7px] font-black text-white uppercase tracking-tighter",
                        isUser ? "bg-rose-500" : "bg-sky-500"
                      )}>
                        {isUser ? 'TI' : 'KUNDËRSHTARI'}
                      </div>
                    </motion.div>
                  );
                })}

                {isTyping && (
                  <motion.div
                    key="typing-indicator"
                    initial={{ opacity: 0, x: 20, scale: 0.9 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9, filter: "blur(4px)", transition: { duration: 0.2 } }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    className="flex-shrink-0 flex items-center gap-3 md:gap-4"
                  >
                    {history.length > 0 && (
                      <div className="text-muted-foreground/20">
                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                      </div>
                    )}
                    <div className="p-3 md:p-5 doodle-border border-dashed border-muted-foreground/20 bg-card/50 font-black handwritten text-xl md:text-3xl uppercase text-muted-foreground/40 flex items-center gap-1 min-w-[80px] md:min-w-[100px]">
                      {typingWord}
                      <motion.div 
                        animate={{ opacity: [1, 0] }}
                        transition={{ repeat: Infinity, duration: 0.5 }}
                        className="w-0.5 md:w-1 h-5 md:h-7 bg-rose-500"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Explanation Overlay */}
          <div className="flex-1 p-4 md:p-8 bg-card relative shadow-inner flex flex-col justify-center overflow-hidden">
            <div className="max-w-md mx-auto text-center space-y-3 w-full">
              <div className="flex flex-col justify-center">
                <AnimatePresence mode="wait">
                  {demoState === 'WON' ? (
                    <motion.div
                      key="winner-msg"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="space-y-3 md:space-y-4"
                    >
                      <div className="inline-block bg-amber-500/10 p-2 md:p-4 rounded-full text-amber-600 dark:text-amber-500 mb-1">
                        <Trophy className="w-8 h-8 md:w-12 md:h-12" />
                      </div>
                      <h3 className="text-xl md:text-3xl font-black text-amber-600 dark:text-amber-500 handwritten uppercase tracking-tight leading-none">Ti Fitove!</h3>
                      <p className="font-bold handwritten text-sm md:text-lg text-muted-foreground leading-snug">
                        Kundërshtarit i mbaroi koha ose nuk gjeti dot fjalë. Fiton ai që thotë fjalën e fundit para se të mbarojë koha!
                      </p>
                    </motion.div>
                  ) : demoState === 'SCORING' ? (
                    <motion.div
                      key="scoring-msg"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-4 md:space-y-5"
                    >
                      <div className="flex justify-center">
                        <div className="bg-sky-500/10 p-2 md:p-4 rounded-full text-sky-500">
                          <Coins className="w-8 h-8 md:w-10 md:h-10" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-xl md:text-2xl font-black text-sky-500 handwritten uppercase tracking-tight leading-none uppercase">Si llogariten pikët?</h3>
                        <p className="font-bold handwritten text-sm md:text-lg text-muted-foreground leading-snug max-w-[300px] md:max-w-none mx-auto italic">
                          Çdo shkronjë vlen <span className="text-rose-500 dark:text-rose-400 font-black">10 pikë</span>. Digrafet (si <span className="text-sky-500 dark:text-sky-400 font-black">LL</span>, <span className="text-sky-500 dark:text-sky-400 font-black">RR</span>, <span className="text-sky-500 dark:text-sky-400 font-black">SH</span>) llogariten si një njësi e vetme!
                        </p>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key={history.length + (isTyping ? '-typing' : '')}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-2 md:space-y-4"
                    >
                      {history.length === 0 && !isTyping ? (
                        <p className="font-black handwritten text-lg md:text-2xl text-muted-foreground italic leading-snug">
                          Loja fillon me një fjalë çfarëdo. <br className="hidden md:block"/> Le të shohim si vazhdon...
                        </p>
                      ) : isTyping ? (
                        <div className="space-y-2">
                          <p className="font-black handwritten text-lg md:text-2xl text-muted-foreground/60">
                            {currentIndexRef.current % 2 === 0 ? 'Ti po shkruan...' : 'Kundërshtari po shkruan...'}
                          </p>
                          {history.length > 0 && (
                            <p className="text-xs md:text-sm font-bold text-rose-500 dark:text-rose-400 uppercase tracking-widest animate-pulse">
                              Fjala duhet të fillojë me &quot;{extractLastUnit(history[history.length-1].word)}&quot;
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3 md:space-y-4">
                          <p className="font-black handwritten text-lg md:text-2xl text-foreground leading-tight uppercase">
                            {history[history.length-1].player === 'USER' ? 'Ti luajte' : 'Kundërshtari luajti'} &quot;<span className={cn(history[history.length-1].player === 'USER' ? "text-rose-500 dark:text-rose-400" : "text-sky-500 dark:text-sky-400")}>{history[history.length-1].word}</span>&quot;
                          </p>
                          <div className="px-2 max-w-[280px] md:max-w-none mx-auto">
                            <p className="font-bold handwritten text-xs md:text-base text-muted-foreground italic leading-snug">
                              Shkronja e fundit është &quot;<span className="text-rose-500 dark:text-rose-400 font-black">{extractLastUnit(history[history.length-1].word)}</span>&quot;, kështu që fjala tjetër duhet të fillojë me këtë shkronjë!
                            </p>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 md:p-6 bg-muted border-t-2 border-border-main/10 shrink-0 flex justify-center gap-3 md:gap-4">
          <motion.button 
            onClick={resetDemo}
            whileHover={{ scale: 1.02, x: -2, y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 max-w-[160px] md:max-w-[180px] flex items-center justify-center gap-2 px-4 md:px-6 py-3 md:py-4 bg-card doodle-border shadow-[4px_4px_0px_0px_#0ea5e9] text-sky-600 dark:text-sky-400 font-black handwritten text-sm md:text-base uppercase tracking-widest transition-all boing"
          >
            <RotateCcw className="w-4 h-4 md:w-5 md:h-5" />
            Rishiko
          </motion.button>
          <motion.button 
            onClick={onClose}
            whileHover={{ scale: 1.02, x: -2, y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 max-w-[160px] md:max-w-[180px] flex items-center justify-center gap-2 px-4 md:px-6 py-3 md:py-4 bg-card doodle-border shadow-[4px_4px_0px_0px_#f43f5e] text-rose-600 dark:text-rose-400 font-black handwritten text-sm md:text-base uppercase tracking-widest transition-all boing"
          >
            <X className="w-4 h-4 md:w-5 md:h-5" />
            Mbyll
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};
